import type { BlogPost, BlogPostMeta } from './types';

/**
 * Parse YAML frontmatter from a markdown string.
 * Returns { meta, content } where content is everything after the closing ---.
 */
function parseFrontmatter(raw: string): { meta: Record<string, string>; content: string } {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) {
    return { meta: {}, content: raw };
  }

  const meta: Record<string, string> = {};
  for (const line of match[1].split('\n')) {
    const idx = line.indexOf(':');
    if (idx > 0) {
      const key = line.slice(0, idx).trim();
      let value = line.slice(idx + 1).trim();
      // Strip surrounding quotes
      if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }
      meta[key] = value;
    }
  }

  return { meta, content: match[2] };
}

function estimateReadingTime(text: string): number {
  const words = text.split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

// Import all markdown files per language using Vite's glob import
const enFiles = import.meta.glob('./en/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;
const ptBRFiles = import.meta.glob('./pt-BR/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;
const ptPTFiles = import.meta.glob('./pt-PT/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;
const deFiles = import.meta.glob('./de/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;
const plFiles = import.meta.glob('./pl/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;

const filesByLang: Record<string, Record<string, string>> = {
  en: enFiles,
  'pt-BR': ptBRFiles,
  'pt-PT': ptPTFiles,
  de: deFiles,
  pl: plFiles,
};

function parseFiles(files: Record<string, string>): BlogPost[] {
  return Object.values(files).map((raw) => {
    const { meta, content } = parseFrontmatter(raw);
    return {
      meta: {
        title: meta.title || '',
        slug: meta.slug || '',
        description: meta.description || '',
        date: meta.date || '',
        author: meta.author || 'Sloty Team',
        keywords: meta.keywords || '',
        readingTime: estimateReadingTime(content),
      },
      content,
    };
  });
}

// Cache parsed posts per language
const cache: Record<string, BlogPost[]> = {};

export function getBlogPosts(lang: string): BlogPost[] {
  // Map language to blog content directory
  const key = lang === 'de' ? 'de' : lang === 'pt-PT' ? 'pt-PT' : lang === 'pt-BR' ? 'pt-BR' : lang === 'pl' ? 'pl' : 'en';

  if (!cache[key]) {
    const files = filesByLang[key] || filesByLang.en;
    cache[key] = parseFiles(files).sort(
      (a, b) => new Date(b.meta.date).getTime() - new Date(a.meta.date).getTime()
    );
  }

  return cache[key];
}

export function getBlogPost(slug: string, lang: string): BlogPost | undefined {
  return getBlogPosts(lang).find((p) => p.meta.slug === slug);
}
